import React from 'react';
import {Form} from 'react-bootstrap';
import Select, {MultiValue} from 'react-select';

export interface MultiSelectOption {
  value: any;
  label: string;
  isDisabled?: boolean;
}

interface MultiSelectFieldProps {
  name: string;
  value: MultiSelectOption[] | null;
  onChange: (value: MultiSelectOption[] | null) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  isRequired?: boolean;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  closeMenuOnSelect?: boolean;
  enableSelectAll?: boolean;
  maxSelectedItems?: number;
  maxMenuHeight?: number;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  menuShouldScrollIntoView?: boolean;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
    name,
    value,
    onChange,
    options,
    placeholder = 'Select options...',
    isRequired,
    error,
    touched,
    disabled = false,
    isSearchable = true,
    isClearable = true,
    closeMenuOnSelect = false,
    enableSelectAll = false,
    maxSelectedItems,
    maxMenuHeight,
    menuPlacement,
    menuShouldScrollIntoView,
}) => {
    const hasError = touched && !!error;

    // Add "Select All" option if enabled
    const selectAllOption: MultiSelectOption = {
        value: '__select_all__',
        label: 'Select All',
    };

    const enhancedOptions = enableSelectAll ? [selectAllOption, ...options] : options;

    const handleChange = (selectedOptions: MultiValue<MultiSelectOption>) => {
        const selectedArray = Array.from(selectedOptions);

        // Handle "Select All" logic
        if (enableSelectAll && selectedArray.some(option => option.value === '__select_all__')) {
            const isSelectingAll = !value || value.length !== options.length;

            if (isSelectingAll) {
                // Select all options (excluding the "Select All" option itself)
                onChange(options);
            } else {
                // Deselect all
                onChange([]);
            }
            return;
        }

        // Handle max selected items
        if (maxSelectedItems && selectedArray.length > maxSelectedItems) {
            return; // Don't allow more than max
        }

        onChange(selectedArray.length > 0 ? selectedArray : null);
    };

    const getDisplayValue = () => {
        if (!value || value.length === 0) {return null;}

        // Filter out any "Select All" options from display
        return value.filter(option => option.value !== '__select_all__');
    };

    const customStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            borderColor: hasError ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: hasError
                ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                : state.isFocused
                    ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                    : 'none',
            '&:hover': {
                borderColor: hasError ? '#dc3545' : '#86b7fe',
            },
        }),
        multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: '#e9ecef',
        }),
        multiValueLabel: (provided: any) => ({
            ...provided,
            color: '#495057',
        }),
        multiValueRemove: (provided: any) => ({
            ...provided,
            color: '#6c757d',
            ':hover': {
                backgroundColor: '#dc3545',
                color: 'white',
            },
        }),
    };

    const formatOptionLabel = (option: MultiSelectOption) => {
        if (enableSelectAll && option.value === '__select_all__') {
            const isAllSelected = Boolean(value && value.length === options.length);
            return (
                <div className="d-flex align-items-center">
                    <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={() => { }} // Handled by main onChange
                        className="mr-2"
                    />
                    {option.label}
                </div>
            );
        }
        return option.label;
    };

    return (
        <div>
            <Select
                name={name}
                isMulti
                value={getDisplayValue()}
                onChange={handleChange}
                options={enhancedOptions}
                placeholder={placeholder}
                isDisabled={disabled}
                isSearchable={isSearchable}
                isClearable={isClearable}
                closeMenuOnSelect={closeMenuOnSelect}
                maxMenuHeight={maxMenuHeight}
                menuPlacement={menuPlacement}
                menuShouldScrollIntoView={menuShouldScrollIntoView}
                styles={customStyles}
                formatOptionLabel={enableSelectAll ? formatOptionLabel : undefined}
                className="react-select-container"
                classNamePrefix="react-select"
            />

            {maxSelectedItems && value && value.length > 0 && (
                <Form.Text className="text-muted">
                    {value.length} of {maxSelectedItems} selected
                </Form.Text>
            )}

            {hasError && (
                <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                    {error}
                </Form.Control.Feedback>
            )}

            {isRequired && (
                <Form.Text className="text-muted">
                    <span className="text-danger">*</span> Required
                </Form.Text>
            )}
        </div>
    );
};

export default MultiSelectField;
